export const DEFAULT_WS_PORT = 3478;
export const DEFAULT_MCP_PORT = 3479;

export const TANDEM_REPO_URL = "https://github.com/bloknayrb/tandem";
export const TANDEM_ISSUES_NEW_URL = `${TANDEM_REPO_URL}/issues/new`;

/**
 * Feature flag for the in-app "bring your own API key" Models registry UI
 * (#1018/#1022). The registry stores provider keys in the OS keychain, but no
 * server-side LLM client consumes them yet (see `src/server/models/api-routes.ts`
 * — "a future LLM client"). Until that lands, offering the picker is a dead end:
 * users configure a key, AI still doesn't work, and they conclude the app is
 * broken. So the entire BYO-models surface (first-run picker, Settings → Models
 * tab, titlebar default-model chip) is gated OFF behind this flag. AI today is
 * the external Claude Code integration (MCP). Flip to `true` — or wire to an
 * env/build define — once the outbound LLM client exists.
 */
export const BYO_MODELS_ENABLED = false;

/** File extensions the server accepts for opening. */
export const SUPPORTED_EXTENSIONS = new Set([".md", ".txt", ".html", ".htm", ".docx"]);

/**
 * Inline marks the `.docx` import path (`docx-html.ts#htmlToYDoc`) can emit onto
 * Y.XmlText. The client Tiptap schema MUST register a mark for every name here:
 * y-prosemirror's sync (`createTextNodesFromYText`) calls `schema.mark(name)` for
 * each delta attribute and, on an UNREGISTERED mark, its catch deletes the whole
 * offending Y.XmlText and propagates the deletion to disk — silent content loss,
 * not a crash. So this list is the contract the editor schema is tested against
 * (`tests/client/editor-schema-marks.test.ts`). Lives in shared (not docx-html)
 * so the client guard imports the real source rather than a drift-prone copy.
 * The markdown path (`mdast-ydoc.ts`) emits a different set (its core + the
 * `rawMarkdown` mark, already client-registered).
 */
export const DOCX_INLINE_MARKS = [
  "bold",
  "italic",
  "strike",
  "code",
  "link",
  "underline",
  "superscript",
  "subscript",
  // Footnote reference marker (#1123 Tier-A #3). Carries `{ id, kind }` on the
  // verbatim `[N]` text mammoth renders, so export can emit a real
  // `<w:footnoteReference>`. The literal "footnote-ref" MUST byte-match the
  // delta-attribute key `docx-html.ts` writes AND the client `Mark.create`
  // name (`footnote-ref.ts`) — a one-char drift is silent XmlText deletion.
  "footnote-ref",
] as const;

// DEFAULT_FONT_BY_EXTENSION removed as a #887 follow-up: seeded defaults
// silently overrode the user's global editor-font choice for un-customized
// formats (changing Settings > Editor Font did nothing for .docx / .html /
// .txt until the user clicked every per-format radio group). The global
// setting is now the true default; `fontByExtension` is a sparse
// user-overrides map.

export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
export const SESSION_MAX_AGE = 30 * 24 * 60 * 60 * 1000; // 30 days
export const TYPING_DEBOUNCE = 3000; // 3 seconds
export const DISCONNECT_DEBOUNCE_MS = 3000; // 3 seconds before showing "server not reachable"
export const PROLONGED_DISCONNECT_MS = 30_000; // 30 seconds before showing App-level disconnect banner

import type { HighlightColor } from "./types.js";

export const HIGHLIGHT_COLORS: Record<HighlightColor, string> = {
  yellow: "rgba(255, 235, 59, 0.3)",
  green: "rgba(76, 175, 80, 0.3)",
  blue: "rgba(33, 150, 243, 0.3)",
  pink: "rgba(236, 72, 153, 0.3)",
};

export const HIGHLIGHT_COLOR_VARS: Record<HighlightColor, string> = {
  yellow: "var(--tandem-highlight-yellow)",
  green: "var(--tandem-highlight-green)",
  blue: "var(--tandem-highlight-blue)",
  pink: "var(--tandem-highlight-pink)",
};

export function normalizeHighlightColor(color: string | null | undefined): HighlightColor {
  return color && color in HIGHLIGHT_COLORS ? (color as HighlightColor) : "yellow";
}

export const TANDEM_MODE_DEFAULT = "tandem" as const;
export const TANDEM_MODE_KEY = "tandem:mode";
export const TANDEM_SETTINGS_KEY = "tandem:settings";
// Panel-width localStorage keys.
//
// NOTE: these use legacy hyphen naming (vs the neighboring colon convention
// `tandem:mode`/`tandem:settings`) because they predate the colon scheme and
// changing the strings would invalidate every existing user's saved widths.
// Do not "fix" the style — the key string is the persistence contract.
//
// Right-side panel width is shared between the tabbed layout and any
// future left-panel variant. The left key only applies when the left
// panel is visible.
export const PANEL_WIDTH_KEY = "tandem-panel-width";
export const LEFT_PANEL_WIDTH_KEY = "tandem-left-panel-width";

export type PanelSide = "left" | "right";

/**
 * Maps a panel side to its localStorage key. Using a Record instead of two
 * bare constants makes the "both handles write to the same key" regression
 * (#228) structurally impossible — you can't accidentally map both sides to
 * the same value at a callsite.
 *
 * Uses `as const satisfies Record<PanelSide, string>` so the value type stays
 * as the literal strings rather than widening to `string` — this preserves
 * the persistence-key identity at every callsite while still enforcing
 * exhaustive coverage of `PanelSide`.
 */
export const PANEL_WIDTH_KEYS = {
  left: LEFT_PANEL_WIDTH_KEY,
  right: PANEL_WIDTH_KEY,
} as const satisfies Record<PanelSide, string>;
export const SELECTION_DWELL_DEFAULT_MS = 1000;
export const SELECTION_DWELL_MIN_MS = 500;
export const SELECTION_DWELL_MAX_MS = 3000;

// Large file thresholds
export const CHARS_PER_PAGE = 3_000;
export const LARGE_FILE_PAGE_THRESHOLD = 50;
export const VERY_LARGE_FILE_PAGE_THRESHOLD = 100;

export const CTRL_ROOM = "__tandem_ctrl__";

/** Y.Map key constants — centralized to prevent silent bugs from string typos. */
export const Y_MAP_ANNOTATIONS = "annotations";
export const Y_MAP_AWARENESS = "awareness";
export const Y_MAP_USER_AWARENESS = "userAwareness";
export const Y_MAP_MODE = "mode";
export const Y_MAP_DWELL_MS = "selectionDwellMs";
export const Y_MAP_CHAT = "chat";
export const Y_MAP_DOCUMENT_META = "documentMeta";
export const Y_MAP_ANNOTATION_REPLIES = "annotationReplies";
export const Y_MAP_SAVED_AT_VERSION = "savedAtVersion";
export const Y_MAP_AUTHORSHIP = "authorship";
// Y.Map sub-keys: userAwareness
export const Y_MAP_SELECTION = "selection";
export const Y_MAP_ACTIVITY = "activity";
// Y.Map sub-keys: awareness (Claude focus)
export const Y_MAP_CLAUDE = "claude";
// Y.Map sub-keys: documentMeta
export const Y_MAP_OPEN_DOCUMENTS = "openDocuments";
export const Y_MAP_ACTIVE_DOCUMENT_ID = "activeDocumentId";
export const Y_MAP_ACTIVE_DOCUMENT_EPOCH = "activeDocumentEpoch";
export const Y_MAP_READ_ONLY = "readOnly";
export const Y_MAP_STORE_READ_ONLY = "storeReadOnly";
/**
 * Per-document external-conflict state (#1069, `.docx` only). Holds an
 * `ExternalConflictState` (see shared/types.ts) while the document's unsaved
 * Y.Doc edits diverge from the on-disk source; absent otherwise. Written via
 * `withInternal` (server metadata); cleared on resolve / reload / explicit save.
 */
export const Y_MAP_EXTERNAL_CONFLICT = "externalConflict";
/**
 * Per-document docx fidelity report (#1145, `.docx` only). Holds a
 * `FidelityReport` (see shared/types.ts): Word features mammoth dropped on
 * import (`importLosses`, set at open / force-reload / file-watcher reload) and
 * what the export downgraded on the most recent save (`exportDowngrades`). The
 * client renders a calm, self-erasing notice while either list is non-empty.
 * No observer is attached to the per-document `documentMeta` map (durable-sync
 * watches only annotations/replies; the only `documentMeta` channel observer is
 * `ctrl-meta` on CTRL_ROOM), so the write is inert at any origin — server
 * write-only, client read-only.
 */
export const Y_MAP_FIDELITY_REPORT = "fidelityReport";
/**
 * Per-document reconstructed Word footnote bodies (#1123 Tier-A #3 PR 2, `.docx`
 * only). Holds `Record<string, FootnoteBody>` (see shared/types.ts) keyed by the
 * OOXML footnote id, written off-fragment under Y_MAP_DOCUMENT_META so the inline
 * `[N]` marker stays offset-neutral (a footnote body is not document body text).
 * Written as a WHOLE-VALUE replace at import (so a force-reload of a doc with
 * fewer footnotes can't leave stale ids) and read by the exporter to emit real
 * `<w:footnote>` parts. Same inertness as Y_MAP_FIDELITY_REPORT: no observer on
 * per-document documentMeta, so the write is server-only, client/Claude-invisible.
 */
export const Y_MAP_FOOTNOTE_BODIES = "footnoteBodies";

export const AUTHORSHIP_TOGGLE_KEY = "tandem:showAuthorship";
/**
 * Per-type annotation decoration visibility, mirrored from settings so the
 * ProseMirror plugin can read it at init before any Svelte effect runs.
 * Value is `JSON.stringify({ comment, highlight, note })` carrying the
 * *effective* booleans (master mute already folded in). Replaces the v8-era
 * single `tandem:showAnnotationDecorations` flag (#596 → 1.13 per-type split).
 */
export const DECORATION_VISIBILITY_KEY = "tandem:decorationVisibility";

export const RECENT_FILES_KEY = "tandem:recentFiles";
export const RECENT_FILES_CAP = 20;

export const USER_NAME_KEY = "tandem:userName";
export const USER_NAME_DEFAULT = "You";
export const USER_NAME_EVENT = "tandem:user-name-changed";
export const USER_NAME_MAX_LEN = 40;

// Toast notifications
export const TOAST_DISMISS_MS = { error: 8000, warning: 6000, info: 4000 } as const;
export const MAX_VISIBLE_TOASTS = 5;
export const NOTIFICATION_BUFFER_SIZE = 50;

// Activity center — persistent notification tray (sub-PR 1.10).
// NOTE: NOT "tandem:activity" — that would shadow the Y_MAP_ACTIVITY = "activity"
// Y.Map sub-key above. This is a localStorage key for the client-side tray history.
export const ACTIVITY_HISTORY_KEY = "tandem:activityHistory";
export const ACTIVITY_HISTORY_CAP = 50;
// Tray-side TTL for info-severity items. Deliberately decoupled from
// TOAST_DISMISS_MS.info: tied together, an info item left the tray the moment
// its toast vanished, so the tray never answered "I missed the toast — what
// just happened?". Warnings/errors persist until dismissed; coalescing + the
// cap keep ambient SSE info from flooding the tray in the meantime.
export const ACTIVITY_INFO_TTL_MS = 5 * 60_000;

// Onboarding tutorial
export const TUTORIAL_COMPLETED_KEY = "tandem:tutorialCompleted";
// Load-bearing: useTutorial.svelte.ts uses this prefix to exclude tutorial
// SEEDS from "user-authored annotation" detection. Tutorial NOTES carry
// author="user" (ADR-027: only the user can author notes), so the prefix
// is the ONLY thing distinguishing a seed from a real user note. Renaming
// this constant without updating useTutorial.svelte.ts would silently
// re-introduce the step-1 auto-advance bug from PR #621 PR-A2b.
export const TUTORIAL_ANNOTATION_PREFIX = "tutorial-";
/** Persists "user skipped the Cowork onboarding step" across sessions. */
export const COWORK_ONBOARDING_SKIPPED_KEY = "tandem:coworkOnboardingSkipped";
/** Polling interval for `cowork_get_status` while the consumer is active. */
export const COWORK_STATUS_POLL_MS = 30_000;
/** Debounce interval for the "Re-scan workspaces" button. */
export const COWORK_RESCAN_DEBOUNCE_MS = 2_000;

// Channel / event queue
export const CHANNEL_EVENT_BUFFER_SIZE = 200;
export const CHANNEL_EVENT_BUFFER_AGE_MS = 60_000; // 60 seconds
export const CHANNEL_SSE_KEEPALIVE_MS = 15_000; // 15 seconds
export const CHANNEL_MAX_RETRIES = 5;
export const CHANNEL_RETRY_DELAY_MS = 2_000;

// Channel shim per-request timeouts. Mirror the monitor pattern (#364) so a
// half-open Tandem server can't wedge `tandem_reply`, the permission relay,
// or the event-bridge SSE handshake / awareness / mode / error-report POSTs.
// Intentionally separate constants per endpoint so a slow endpoint doesn't
// hold up a faster one — and so log lines name a meaningful threshold.
export const CHANNEL_CONNECT_FETCH_TIMEOUT_MS = 10_000; // /api/events handshake
export const CHANNEL_SSE_INACTIVITY_TIMEOUT_MS = 60_000; // No-bytes watchdog on SSE body
export const CHANNEL_MODE_FETCH_TIMEOUT_MS = 2_000; // /api/mode cache refresh
export const CHANNEL_AWARENESS_FETCH_TIMEOUT_MS = 5_000; // /api/channel-awareness POST
export const CHANNEL_ERROR_REPORT_TIMEOUT_MS = 3_000; // /api/channel-error POST on exit
export const CHANNEL_REPLY_FETCH_TIMEOUT_MS = 5_000; // /api/channel-reply (tandem_reply)
export const CHANNEL_PERMISSION_FETCH_TIMEOUT_MS = 5_000; // /api/channel-permission relay
// Bound the SSE buffer so a misbehaving server that never emits frame
// boundaries can't wedge the bridge with unbounded string growth.
export const CHANNEL_MAX_SSE_BUFFER_BYTES = 1_000_000;

/** Auth token filename inside the app-data directory. */
export const TOKEN_FILE_NAME = "auth-token";

/** Default MCP bind host — loopback only by default. */
export const DEFAULT_BIND_HOST = "127.0.0.1";

/** Env var name to opt in to unauthenticated LAN binding. */
export const TANDEM_ALLOW_UNAUTHENTICATED_LAN_ENV = "TANDEM_ALLOW_UNAUTHENTICATED_LAN";

/**
 * Env var name to suppress the integration-wizard first-run auto-open.
 * Used by the E2E harness (Playwright) so wizard modals don't cover
 * unrelated editor surfaces. The integration-wizard.spec.ts test itself
 * does NOT set this — it exercises the manual-reopen affordance.
 */
export const TANDEM_DISABLE_FIRST_RUN_WIZARD_ENV = "TANDEM_DISABLE_FIRST_RUN_WIZARD";

/** Tauri WebView origin hostname — must be accepted alongside localhost. */
export const TAURI_HOSTNAME = "tauri.localhost";

/**
 * Linux Tauri WebView origin. On Linux the WebView serves content from the
 * custom `tauri://` scheme (origin `tauri://localhost`), not `http://tauri.localhost`
 * as on Windows. The `tauri://` scheme is non-network and served only by the
 * Tauri runtime's own WebView, so no remote page can forge this origin — it is
 * the unforgeable Linux analog of the trusted Windows origin. Match the exact
 * string only (never a `tauri://*` wildcard).
 */
export const TAURI_LINUX_ORIGIN = "tauri://localhost";

// Zoom persistence (Tauri desktop)
export const ZOOM_STORAGE_KEY = "tandem:zoomLevel";
export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 2.0;
export const ZOOM_DEFAULT = 1.0;
